<!--
  Reusable field-level change history modal (Enhanced Audit Trail).
  Usage:
    <audit-history-modal
      v-model="showHistory"
      entity-type="Zone"
      :record-id="props.id"
      title="Zone Change History" />
-->
<template>
  <v-dialog v-model="open" max-width="900" scrollable>
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2">mdi-history</v-icon>
        <span>{{ title }}</span>
        <v-spacer />
        <v-btn
          size="small"
          variant="text"
          :disabled="!items.length || exporting"
          :loading="exporting"
          @click="onExport"
        >
          <v-icon left size="18">mdi-download</v-icon>
          Export CSV
        </v-btn>
        <v-btn icon variant="text" @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text style="min-height: 200px">
        <div v-if="loading" class="d-flex justify-center pa-6">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <div v-else-if="error" class="text-error pa-4">{{ error }}</div>

        <div v-else-if="!items.length" class="text-medium-emphasis pa-4 text-center">
          No change history recorded yet.
        </div>

        <div v-else>
          <v-card
            v-for="item in items"
            :key="item.auditChangeLogId"
            variant="outlined"
            class="mb-3"
          >
            <v-card-text class="pb-2">
              <div class="d-flex justify-space-between align-center mb-2 flex-wrap">
                <div class="font-weight-medium">{{ item.summary || item.action }}</div>
                <div class="text-caption text-medium-emphasis">
                  {{ formatDate(item.changedDateTime) }} · {{ item.changedBy }}
                </div>
              </div>

              <v-table density="compact" class="audit-table" v-if="item.changes && item.changes.length">
                <thead>
                  <tr>
                    <th class="text-left">FIELD</th>
                    <th class="text-left">OLD VALUE</th>
                    <th class="text-left">NEW VALUE</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in item.changes" :key="i">
                    <td class="font-weight-bold">{{ c.fieldName }}</td>
                    <td class="text-medium-emphasis">{{ display(c.oldValue) }}</td>
                    <td>{{ display(c.newValue) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-card-text>
          </v-card>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch } from "vue";
import { fetchAuditHistory, exportAuditHistory } from "@/api/auditChanges";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  entityType: { type: String, required: true },
  recordId: { type: [String, Number], default: null },
  title: { type: String, default: "Change History" },
});
const emit = defineEmits(["update:modelValue"]);

const open = ref(props.modelValue);
const items = ref([]);
const loading = ref(false);
const exporting = ref(false);
const error = ref("");

watch(
  () => props.modelValue,
  (v) => {
    open.value = v;
    if (v) load();
  }
);
watch(open, (v) => emit("update:modelValue", v));

async function load() {
  if (!props.recordId) return;
  loading.value = true;
  error.value = "";
  try {
    items.value = await fetchAuditHistory(props.entityType, props.recordId);
  } catch (e) {
    error.value = e?.response?.data?.message || e?.message || "Failed to load history.";
  } finally {
    loading.value = false;
  }
}

async function onExport() {
  if (!props.recordId) return;
  exporting.value = true;
  try {
    const blob = await exportAuditHistory(props.entityType, props.recordId);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit_${props.entityType}_${props.recordId}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } finally {
    exporting.value = false;
  }
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-IE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function display(value) {
  return value === null || value === undefined || value === "" ? "—" : value;
}
</script>

<style scoped>
.audit-table :deep(thead th) {
  background-color: #f0f1f3;
  font-weight: 600;
  color: #5a6472;
  letter-spacing: 0.02em;
}
</style>
